import { useState, useEffect, useMemo } from 'react';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { GridComponent, ColumnsDirective, ColumnDirective } from '@syncfusion/ej2-react-grids';
import { useTranslation } from 'react-i18next';
import { ResourceTypeControllerService, type ResourceTypeDto } from '../../api';
import { MaterialControllerService, type MaterialDto } from '../../api';
import { OvenSetupTimeControllerService } from '../../api';

interface SetupTimeMatrixRow {
  fromProductUuid: string;
  fromProductName: string;
  [key: string]: string | number; // Dynamic product columns
}

const OvenSetupTimesPage = () => {
  const { t } = useTranslation(['common']);
  const [selectedResourceTypeUuid, setSelectedResourceTypeUuid] = useState<string | null>(null);
  const [resourceTypes, setResourceTypes] = useState<ResourceTypeDto[]>([]);
  const [allProducts, setAllProducts] = useState<MaterialDto[]>([]);
  const [setupTimeMatrix, setSetupTimeMatrix] = useState<SetupTimeMatrixRow[]>([]);
  const [loading, setLoading] = useState(false);

  // Load resource types (OVEN category only)
  useEffect(() => {
    const loadResourceTypes = async () => {
      try {
        const allResourceTypes = await ResourceTypeControllerService.findAll();
        const ovenResourceTypes = allResourceTypes.filter(rt => rt.category === 'OVEN');
        setResourceTypes(ovenResourceTypes);
      } catch (error) {
        console.error('Error loading resource types:', error);
      }
    };
    loadResourceTypes();
  }, []);

  // Load all products
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const allMaterials = await MaterialControllerService.findAll();
        const productMaterials = allMaterials.filter(m => m.materialType === 'PRODUCT');
        setAllProducts(productMaterials);
      } catch (error) {
        console.error('Error loading products:', error);
      }
    };
    loadProducts();
  }, []);

  // Filter products based on selected resource type's UUID (matching defaultOvenTypeUuid)
  const products = useMemo(() => {
    if (!selectedResourceTypeUuid || allProducts.length === 0) {
      return [];
    }
    // Filter products where defaultOvenTypeUuid matches the selected resource type UUID
    return allProducts.filter(product => product.defaultOvenTypeUuid === selectedResourceTypeUuid);
  }, [selectedResourceTypeUuid, allProducts]);

  // Calculate setup times when resource type or products change
  useEffect(() => {
    if (!selectedResourceTypeUuid || products.length === 0) {
      setSetupTimeMatrix([]);
      return;
    }

    const calculateSetupTimes = async () => {
      setLoading(true);
      try {
        // Prepare all product combinations for batch request
        const productCombinations: Array<{ fromProductId: string; toProductId: string }> = [];
        for (const fromProduct of products) {
          if (!fromProduct.uuid) continue;
          for (const toProduct of products) {
            if (!toProduct.uuid) continue;
            productCombinations.push({
              fromProductId: fromProduct.uuid,
              toProductId: toProduct.uuid
            });
          }
        }

        // Call batch API to calculate all setup times at once
        const response = await OvenSetupTimeControllerService.calculateSetupTimeBatch({
          resourceTypeUuid: selectedResourceTypeUuid,
          productCombinations: productCombinations
        });

        // Create a map for quick lookup: fromProductId -> toProductId -> setupTimeSeconds
        const setupTimeMap = new Map<string, Map<string, number>>();
        for (const result of response.results ?? []) {
          if (!result.fromProductId || !result.toProductId) continue;
          if (!setupTimeMap.has(result.fromProductId)) {
            setupTimeMap.set(result.fromProductId, new Map());
          }
          setupTimeMap.get(result.fromProductId)!.set(result.toProductId, result.setupTimeSeconds || 0);
        }

        // Create matrix structure: each row is a "from" product, each column is a "to" product
        const matrix: SetupTimeMatrixRow[] = [];
        for (const fromProduct of products) {
          if (!fromProduct.uuid) continue;
          
          const row: SetupTimeMatrixRow = {
            fromProductUuid: fromProduct.uuid,
            fromProductName: fromProduct.name || ''
          };
          
          // Get setup times from the map
          const fromProductMap = setupTimeMap.get(fromProduct.uuid);
          for (const toProduct of products) {
            if (!toProduct.uuid) continue;
            const setupTimeSeconds = fromProductMap?.get(toProduct.uuid) ?? 0;
            row[toProduct.uuid] = setupTimeSeconds;
          }
          
          matrix.push(row);
        }
        
        setSetupTimeMatrix(matrix);
      } catch (error) {
        console.error('Error calculating setup times:', error);
      } finally {
        setLoading(false);
      }
    };

    calculateSetupTimes();
  }, [selectedResourceTypeUuid, products]);

  const selectedResourceType = useMemo(() => {
    return resourceTypes.find(rt => rt.uuid === selectedResourceTypeUuid) || null;
  }, [resourceTypes, selectedResourceTypeUuid]);

  const resourceTypeDataSource = resourceTypes.map(rt => ({
    text: rt.name || '',
    value: rt.uuid || ''
  }));

  // Helper function to format seconds as HH:MM:SS
  const formatDuration = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // Calculate min and max values for heatmap
  const { minValue, maxValue } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    
    setupTimeMatrix.forEach(row => {
      products.forEach(product => {
        if (product.uuid) {
          const value = row[product.uuid] as number;
          if (value !== undefined && value !== null && value > 0) {
            min = Math.min(min, value);
            max = Math.max(max, value);
          }
        }
      });
    });
    
    return {
      minValue: min === Infinity ? 0 : min,
      maxValue: max === -Infinity ? 0 : max
    };
  }, [setupTimeMatrix, products]);

  // Helper function to get heatmap color based on value
  const getHeatmapColor = (value: number): string => {
    if (value === 0 || value === null || value === undefined || maxValue === 0) {
      return 'transparent'; // No color for zero values
    }
    
    // Normalize value between 0 and 1
    const normalized = (value - minValue) / (maxValue - minValue || 1);
    
    // Interpolate between green (low) and red (high)
    // Green: rgb(34, 197, 94) - green-500
    // Red: rgb(239, 68, 68) - red-500
    const red = Math.round(34 + (239 - 34) * normalized);
    const green = Math.round(197 - (197 - 68) * normalized);
    const blue = Math.round(94 - (94 - 68) * normalized);
    
    return `rgb(${red}, ${green}, ${blue})`;
  };

  return (
    <div className="p-4 flex flex-col h-full">
      <h2 className="text-xl font-semibold mb-4">{t('ovenSetupTimes.title')}</h2>
      
      <div className="mb-4 w-80">
        <DropDownListComponent
          dataSource={resourceTypeDataSource}
          fields={{ text: 'text', value: 'value' }}
          placeholder={t('ovenSetupTimes.selectResourceType')}
          floatLabelType="Auto"
          value={selectedResourceTypeUuid || ''}
          change={(e: any) => {
            setSelectedResourceTypeUuid(e.value || null);
          }}
        />
      </div>

      {!selectedResourceTypeUuid && (
        <div className="text-gray-500">{t('ovenSetupTimes.noResourceTypeSelected')}</div>
      )}

      {selectedResourceTypeUuid && products.length === 0 && (
        <div className="text-gray-500">{t('ovenSetupTimes.noProducts')}</div>
      )}

      {selectedResourceTypeUuid && products.length > 0 && (
        <>
          {selectedResourceType && (
            <div className="mb-4 text-sm text-gray-600">
              <div>
                <strong>{t('resourceType.form.heatingPhaseSecondsPerDegree')}:</strong>{' '}
                {selectedResourceType.heatingPhaseSecondsPerDegree != null
                  ? `${selectedResourceType.heatingPhaseSecondsPerDegree} s/°C`
                  : t('actions.notSet')}
              </div>
              <div>
                <strong>{t('resourceType.form.coolingPhaseSecondsPerDegree')}:</strong>{' '}
                {selectedResourceType.coolingPhaseSecondsPerDegree != null
                  ? `${selectedResourceType.coolingPhaseSecondsPerDegree} s/°C`
                  : t('actions.notSet')}
              </div>
            </div>
          )}

          {loading && (
            <div className="mb-4 text-gray-500">{t('actions.loading')}</div>
          )}

          {!loading && setupTimeMatrix.length > 0 && products.length > 0 && (
            <div className="flex-1" style={{ height: 'calc(100vh - 300px)', minHeight: '400px' }}>
              <style>{`
                .e-grid .e-row:nth-child(even) {
                  background-color: #e5e7eb;
                }
                .e-grid .e-row:nth-child(odd) {
                  background-color: #ffffff;
                }
                .e-grid .e-row:hover {
                  background-color: inherit !important;
                }
                .e-grid .e-rowcell:hover {
                  background-color: inherit !important;
                }
              `}</style>
              <GridComponent
                dataSource={setupTimeMatrix}
                height="100%"
                allowSorting={false}
                allowFiltering={false}
                locale="de-DE"
                enableHover={false}
              >
                <ColumnsDirective>
                  <ColumnDirective
                    field="fromProductName"
                    headerText=""
                    width={200}
                    freeze="Left"
                  />
                  {products.map((product) => (
                    <ColumnDirective
                      key={product.uuid}
                      field={product.uuid || ''}
                      headerText={product.name || ''}
                      width={100}
                      textAlign="Center"
                      headerTemplate={() => {
                        const productName = product.name || '';
                        return <span title={productName} style={{ cursor: 'help' }}>{productName}</span>;
                      }}
                      template={(props: any) => {
                        const seconds = props[product.uuid || ''] as number;
                        const backgroundColor = getHeatmapColor(seconds);
                        const textColor = seconds > 0 && maxValue > 0 ? '#ffffff' : '#6b7280';
                        
                        if (seconds === undefined || seconds === null || seconds === 0) {
                          return <span className="text-gray-500">0:00:00</span>;
                        }
                        return (
                          <span 
                            style={{ 
                              backgroundColor: backgroundColor,
                              color: textColor,
                              padding: '2px 6px',
                              borderRadius: '3px',
                              display: 'inline-block',
                              minWidth: '60px'
                            }}
                          >
                            {formatDuration(seconds)}
                          </span>
                        );
                      }}
                    />
                  ))}
                </ColumnsDirective>
              </GridComponent>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OvenSetupTimesPage;
